import React, { type HTMLAttributes, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { getInputColors } from '@wecasa/common/src/components/input/styles';
import colors from '@wecasa/common/src/utils/colors';

import useBoolean from '@wecasa/react-hooks/src/use-boolean';

import { baseInputStyle, Container, InputContainer, InputFrame, type InputStyleProps } from '../input/components';
import AnimatedPlaceholder from '../input/components/animated-placeholder';
import BottomMessage from '../input/components/bottom-message';
import InputIcon from '../input/components/input-icon';
import type { WithClassNameProps } from '../types';
import { StylelessSelect } from '../utils';

const PLACEHOLDER_VALUE = '';

const StyledSelect = styled(StylelessSelect)<InputStyleProps>`
  ${baseInputStyle}
`;

const StyledInputContainer = styled(InputContainer)`
  display: flex;
  justify-items: center;
`;

// Must be position absolute because, unlike input, select component cannot be opened by clicking on a label.
const StyledInputIcon = styled(InputIcon)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

export type SelectProps = WithClassNameProps & {
  ariaLabel?: HTMLAttributes<HTMLSelectElement>['aria-label'];
  disabled?: boolean;
  error?: string;
  id: string;
  label: string;
  message?: string;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onUnmount?: () => void;
  options: {
    name: string;
    value: string;
  }[];
  showArrow?: boolean;
  value?: string;
};

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      ariaLabel,
      error,
      id,
      label,
      name,
      message,
      onChange,
      options,
      showArrow = true,
      value,
      disabled = false,
      className,
      onUnmount,
    },
    ref,
  ) => {
    const hasLabel = !!label;
    const hasValue = !!value;
    const { value: isFocused, setTrue: handleFocus, setFalse: handleBlur } = useBoolean(false);

    useEffect(() => () => onUnmount?.(), [onUnmount]);

    const inputColors = getInputColors({ error, hasValue, isFocused });

    const allOptions = useMemo(
      () => [
        <option key='_placeholder' value={PLACEHOLDER_VALUE}>
          {PLACEHOLDER_VALUE}
        </option>,
        ...options.map(({ name: optionName, value: optionValue }) => (
          <option key={optionValue} value={optionValue}>
            {optionName}
          </option>
        )),
      ],
      [options],
    );

    return (
      <Container $isDisabled={disabled} className={className}>
        <InputFrame $inputId={id} $hasHoverStyle={!disabled && !isFocused && !error} $inputColors={inputColors}>
          <StyledInputContainer>
            <StyledSelect
              aria-label={ariaLabel ?? label}
              name={name}
              ref={ref}
              id={id}
              onBlur={handleBlur}
              onChange={onChange}
              onFocus={handleFocus}
              value={value || PLACEHOLDER_VALUE}
              disabled={disabled}
              $isFocused={isFocused}
              $hasLabel={hasLabel}
            >
              {allOptions}
            </StyledSelect>
            {hasLabel && (
              <AnimatedPlaceholder
                color={inputColors.placeholder}
                inputId={id}
                isActive={isFocused || hasValue}
                label={label}
              />
            )}
          </StyledInputContainer>
          {!disabled && showArrow && (
            <StyledInputIcon
              inputId={id}
              name={isFocused ? 'chevron-up' : 'chevron-down'}
              size={12}
              color={isFocused ? colors.orange100 : inputColors.icon}
            />
          )}
        </InputFrame>
        <BottomMessage error={error} message={message} />
      </Container>
    );
  },
);

export default Select;
